// src/scene/wallLayout.ts
import * as THREE from 'three';
import type { Manifest, Work } from '../manifest';
import { layoutCard, type CardLayout } from './cardLayout';

/** Wall is the plane X=0, facing the viewer on +X. The wall is TALL: works at
 *  (0, y, z) stack upward in Y (the long axis you climb) and spread across a
 *  bounded width in Z. Clusters stack bottom→top. */
export const WALL_WIDTH = 9.5;    // bounded horizontal span (Z); ~5–6 cards wide — a broad gallery wall
export const BAND_GAP = 1.4;      // Y gap between stacked cluster sections
export const CARD_GAP = 0.08;     // gap between card footprints (mats also separated by their shadow margins)
export const FLOOR_CLEARANCE = 0.85; // lowest row hangs this high → bottom row centres ≈ eye level (~1.7m)

/** Deterministic size class per work id (salon variation, no runtime randomness). */
export function sizeClass(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return [0.82, 1.0, 1.28][h % 3];
}

export interface WorkPlacement {
  work: Work;
  cluster: string;
  position: THREE.Vector3; // world center on the wall (x = 0)
  scale: number;           // card scale (size variation)
  layout: CardLayout;      // outer/inner/art proportions from cardLayout
}

export interface Band {
  name: string;
  color: THREE.Color;
  yStart: number;
  yEnd: number;
  center: THREE.Vector3;   // band center on the wall (for HUD fly-to + motes)
  works: Work[];
}

interface PackResult { placements: WorkPlacement[]; yEnd: number; }

/** Row packer: works fill a row left→right across Z; when the next card would
 *  exceed WALL_WIDTH, start a new row higher in Y. Within a row cards are
 *  Z-disjoint; rows are Y-disjoint → no overlaps. Deterministic. */
export function packBand(works: Work[], yStart: number, cluster = ''): PackResult {
  const placements: WorkPlacement[] = [];
  let rowStart = yStart;       // y of the current row's bottom edge
  let rowHeight = 0;           // max card height in the current row
  let zCursor = 0;             // filled width across Z in the current row
  let buffer: { work: Work; scale: number; layout: CardLayout; z: number; w: number; h: number }[] = [];

  const flush = () => {
    if (buffer.length === 0) return;
    const yc = rowStart + rowHeight / 2; // centre cards vertically within the row
    for (const b of buffer) {
      placements.push({
        work: b.work, cluster, scale: b.scale, layout: b.layout,
        position: new THREE.Vector3(0, yc, b.z + b.w / 2),
      });
    }
    rowStart += rowHeight + CARD_GAP;
    rowHeight = 0; zCursor = 0; buffer = [];
  };

  for (const w of works) {
    const layout = layoutCard(w.aspect);
    const scale = sizeClass(w.id);
    const cw = layout.cardW * scale;
    const ch = layout.cardH * scale;
    if (zCursor > 0 && zCursor + cw > WALL_WIDTH) flush();
    buffer.push({ work: w, scale, layout, z: zCursor, w: cw, h: ch });
    rowHeight = Math.max(rowHeight, ch);
    zCursor += cw + CARD_GAP;
  }
  flush();
  return { placements, yEnd: Math.max(yStart, rowStart - CARD_GAP) };
}


export interface WallLayout {
  works: WorkPlacement[];
  bands: Band[];
  height: number;   // total tall height (Y) — the climb
  width: number;    // bounded across span (Z)
}

export function wallLayout(manifest: Manifest): WallLayout {
  const works: WorkPlacement[] = [];
  const bands: Band[] = [];
  let y = FLOOR_CLEARANCE;   // first row hangs at gallery height, not from the floor
  for (const name of manifest.clusters) {
    const clusterWorks = manifest.works.filter((w) => w.cluster === name);
    const color = new THREE.Color(clusterWorks[0]?.palette[0] ?? '#888888');
    const yStart = y;
    const { placements, yEnd } = packBand(clusterWorks, yStart, name);
    works.push(...placements);
    bands.push({
      name, color, yStart, yEnd,
      center: new THREE.Vector3(0, (yStart + yEnd) / 2, WALL_WIDTH / 2),
      works: clusterWorks,
    });
    y = yEnd + BAND_GAP;
  }
  return { works, bands, height: bands.length ? y : 0, width: WALL_WIDTH };
}
